// Local imports
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import outlook from './outlook';
import gmail from './gmail';
import secureCache from './secureCache';
import { red, yellow } from '@mui/material/colors';
import { faFlag, faStar } from '@fortawesome/pro-solid-svg-icons';
import { faStar as faStarLight, faFlag as faFlagLight } from '@fortawesome/pro-light-svg-icons';

/**
 * Get the session ID for the current email provider
 */
// DynamoDB caps a queued email at 400KB; leave room for the rest of the row and for the
// signature the worker appends after the message is queued.
const MAX_QUEUED_EMAIL_BYTES = 300 * 1024;

function byteLength(text) {
    if (!text) return 0;
    // Bytes, not characters - one emoji or accented character is several bytes, and it is
    // bytes the limit is measured in.
    return typeof TextEncoder !== 'undefined' ? new TextEncoder().encode(String(text)).length : String(text).length;
}

function formatSize(bytes) {
    return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

function getSessionId(org) {
    if (org.integrations.email.supplier === 'outlook') {
        const sessionId = window.localStorage.getItem('microsoft-session-id') || uuidv4();
        window.localStorage.setItem('microsoft-session-id', sessionId);
        return sessionId;
    } else if (org.integrations.email.supplier === 'gmail') {
        const sessionId = window.localStorage.getItem('google-session-id') || uuidv4();
        window.localStorage.setItem('google-session-id', sessionId);
        return sessionId;
    }
    return null;
}

/**
 * Queue an email for server-side sending
 */
async function queueEmail({
    org,
    toRecipients,
    ccRecipients,
    bccRecipients,
    subject,
    body,
    attachments = [],
    isReply = false,
    conversationId = null,
    generatePdf = null,
    opportunityId = null,
    includeThread = true,
    includeSignature = true,
    logFollowUp = false,
    monadaAccesstoken
}) {
    if (!org?.integrations?.email?.enabled) return null;

    const sessionId = getSessionId(org);
    const provider = org.integrations.email.supplier;

    // Format recipients for the API
    const to = (toRecipients || []).map(r => ({
        address: r.address || r,
        name: r.name || undefined
    }));

    const cc = (ccRecipients || []).map(r => ({
        address: r.address || r,
        name: r.name || undefined
    }));

    const bcc = (bccRecipients || []).map(r => ({
        address: r.address || r,
        name: r.name || undefined
    }));

    // The queue stores the message as a single DynamoDB item, and DynamoDB caps an item at
    // 400KB. Over that the write is simply rejected, which surfaces as a 500 with nothing
    // to explain it - so stop here instead, where we can say what happened and why.
    //
    // The headroom is for everything else on the row: recipients, subject, attachment
    // metadata, and the signature the worker appends after we hand it over.
    const bodyBytes = byteLength(body) + byteLength(subject) + byteLength(JSON.stringify(attachments || []));
    if (bodyBytes > MAX_QUEUED_EMAIL_BYTES) {
        throw new Error(
            `This email is ${formatSize(bodyBytes)}, which is over the ${formatSize(MAX_QUEUED_EMAIL_BYTES)} limit and cannot be sent. ` +
            'This is almost always a very large pasted table or image. Trim it down, or attach it as a file instead.'
        );
    }

    const response = await axios.post(
        `/api/emailqueue?organizationId=${org.id}&accesstoken=${monadaAccesstoken}`,
        {
            sessionId,
            provider,
            to,
            cc,
            ...(bcc.length > 0 ? { bcc } : {}),
            subject,
            body,
            attachments,
            isReply,
            conversationId,
            generatePdf,
            opportunityId,
            includeThread,
            ...(includeSignature === false ? { includeSignature: false } : {}),
            ...(logFollowUp ? { logFollowUp: true } : {})
        }
    );

    // Dispatch event to notify sidebar that a new email was queued
    // Include full email info so sidebar can show it immediately
    window.dispatchEvent(new CustomEvent('email-queued', {
        detail: {
            ...response.data,
            subject,
            to,
            cc,
            provider,
            isReply
        }
    }));

    return response.data;
}

/**
 * Get the list of queued/sent emails for the current user
 */
async function getQueuedEmails({ org, monadaAccesstoken, limit = 20 }) {
    if (!org?.integrations?.email?.enabled) return [];

    const response = await axios.get(
        `/api/emailqueue?organizationId=${org.id}&accesstoken=${monadaAccesstoken}&limit=${limit}`
    );

    return response.data;
}

/**
 * Retry a failed email
 */
async function retryEmail({ org, emailId, monadaAccesstoken }) {
    if (!org?.integrations?.email?.enabled) return null;

    const response = await axios.post(
        `/api/emailqueue/${emailId}/retry?organizationId=${org.id}&accesstoken=${monadaAccesstoken}`
    );

    return response.data;
}

/**
 * Get the status of a single queued email
 */
async function getEmailStatus({ org, emailId, monadaAccesstoken }) {
    if (!org?.integrations?.email?.enabled) return null;

    const response = await axios.get(
        `/api/emailqueue/${emailId}?organizationId=${org.id}&accesstoken=${monadaAccesstoken}`
    );

    return response.data;
}

/**
 * Wait for an email to be sent (polls until sent or error)
 * Returns the final email status
 */
async function waitForEmailSent({ org, emailId, monadaAccesstoken, onStatusChange, pollInterval = 1000, maxWait = 60000 }) {
    if (!org?.integrations?.email?.enabled) return null;

    const startTime = Date.now();

    while (Date.now() - startTime < maxWait) {
        const status = await getEmailStatus({ org, emailId, monadaAccesstoken });

        if (onStatusChange) {
            onStatusChange(status);
        }

        if (status.status === 'sent') {
            return { success: true, email: status };
        }

        if (status.status === 'error') {
            return { success: false, email: status, error: status.error };
        }

        // Still pending or processing, wait and poll again
        await new Promise(resolve => setTimeout(resolve, pollInterval));
    }

    // Timed out
    return { success: false, error: 'Timed out waiting for email to send', timedOut: true };
}

function refreshEmailConnection({ org, accesstoken }) {
    if (!org?.integrations?.email?.enabled) return null;

    if (org.integrations.email.supplier === 'outlook') {
        return outlook.refreshConnection({ organizationId: org.id, accesstoken });
    } else if (org.integrations.email.supplier === 'gmail') {
        return gmail.refreshConnection({ organizationId: org.id, accesstoken });
    }
}

function forceRefreshEmailConnection({ org, accesstoken }) {
    if (!org?.integrations?.email?.enabled) return null;

    if (org.integrations.email.supplier === 'outlook') {
        return outlook.forceRefreshConnection({ organizationId: org.id, accesstoken });
    } else if (org.integrations.email.supplier === 'gmail') {
        return gmail.forceRefreshConnection({ organizationId: org.id, accesstoken });
    }
}

function setConversationsListener(props) {
    const { organization, listener } = props;
    if (!organization?.integrations?.email?.enabled) return null;

    if (organization.integrations.email.supplier === 'outlook') {
        outlook.setConversationsListener(listener);
    } else if (organization.integrations.email.supplier === 'gmail') {
        gmail.setConversationsListener(listener);
    }
}

function setExpiredListener(props) {
    const { organization, listener } = props;
    if (!organization?.integrations?.email?.enabled) return null;

    if (organization.integrations.email.supplier === 'outlook') {
        outlook.setExpiredListener(listener);
    } else if (organization.integrations.email.supplier === 'gmail') {
        gmail.setExpiredListener(listener);
    }
}

function disconnect({ org, accesstoken }) {
    if (!org?.integrations?.email?.enabled) return null;

    if (org.integrations.email.supplier === 'outlook') {
        return outlook.disconnect({ organizationId: org.id, accesstoken });
    } else if (org.integrations.email.supplier === 'gmail') {
        return gmail.disconnect({ organizationId: org.id, accesstoken });
    }
}

function LoginButton(props) {
    const { org, accesstoken, onDone, sx } = props;
    if (!org?.integrations?.email?.enabled) return null;

    if (org.integrations.email.supplier === 'outlook') {
        return (
                <outlook.LoginButton organizationId={org.id} accesstoken={accesstoken} onDone={onDone} sx={sx} />
        );
    } else if (org.integrations.email.supplier === 'gmail') {
        return (
            <gmail.LoginButton organizationId={org.id} accesstoken={accesstoken} onDone={onDone} sx={sx} />
        );
    }
}

// Email conversations cache - using secure IndexedDB cache
const CACHE_MAX_AGE = 5 * 60 * 1000; // 5 minutes

async function getCachedConversations({ encryptionKey, orgId, folder }) {
    return secureCache.getCache({
        encryptionKey,
        orgId,
        type: 'conversations',
        folder,
        maxAge: CACHE_MAX_AGE
    });
}

async function setCachedConversations({ encryptionKey, orgId, folder, data }) {
    return secureCache.setCache({
        encryptionKey,
        orgId,
        type: 'conversations',
        folder,
        data
    });
}

async function getConversations({ org, tokens, folder, search, nextPageToken }) {
    if (!org?.integrations?.email?.enabled) return null;
    if (org.integrations.email.supplier === 'outlook') {
        return outlook.getConversations({ accesstoken: tokens.accessToken, folder, search, nextPageToken });
    } else if (org.integrations.email.supplier === 'gmail') {
        return gmail.getConversations({ accesstoken: tokens.access_token, folder, search, nextPageToken });
    }
}

async function getConversationsWithCache({ org, tokens, folder, search, nextPageToken, encryptionKey, onCacheHit }) {
    if (!org?.integrations?.email?.enabled) return null;

    // Only use cache for initial load (no search, no pagination)
    const canUseCache = !search && !nextPageToken && encryptionKey;

    // Check cache first (async)
    if (canUseCache) {
        try {
            const cached = await getCachedConversations({
                encryptionKey,
                orgId: org.id,
                folder
            });
            if (cached && cached.data) {
                console.log('[email] Cache hit, age:', Math.round(cached.age / 1000), 'seconds');
                // Notify caller about cached data
                if (onCacheHit) {
                    onCacheHit(cached.data);
                }
            }
        } catch (e) {
            console.error('[email] Error reading cache:', e);
        }
    }

    // Fetch fresh data
    const freshData = await getConversations({ org, tokens, folder, search, nextPageToken });

    // Update cache with fresh data (only for initial load)
    if (canUseCache && freshData) {
        setCachedConversations({
            encryptionKey,
            orgId: org.id,
            folder,
            data: freshData
        }).catch(e => console.error('[email] Error writing cache:', e));
    }

    return freshData;
}

function getMessagesForConversation({ org, conversationId, internetMessageId, tokens }) {
    if (!org?.integrations?.email?.enabled) return null;

    if (org.integrations.email.supplier === 'outlook') {
        return outlook.getMessagesForConversation({ conversationId, internetMessageId, accesstoken: tokens.accessToken });
    } else if (org.integrations.email.supplier === 'gmail') {
        return gmail.getMessagesForConversation({ conversationId, internetMessageId, accesstoken: tokens.access_token });
    }
}

function markAsRead({ org, messageIds, tokens }) {
    if (!org?.integrations?.email?.enabled) return Promise.resolve();

    if (org.integrations.email.supplier === 'outlook') {
        return outlook.markAsRead({ messageIds, accesstoken: tokens.accessToken });
    } else if (org.integrations.email.supplier === 'gmail') {
        return gmail.markAsRead({ messageIds, accesstoken: tokens.access_token });
    }
    return Promise.resolve();
}

function markAsUnread({ org, messageIds, tokens }) {
    if (!org?.integrations?.email?.enabled) return Promise.resolve();

    if (org.integrations.email.supplier === 'outlook') {
        return outlook.markAsUnread({ messageIds, accesstoken: tokens.accessToken });
    } else if (org.integrations.email.supplier === 'gmail') {
        return gmail.markAsUnread({ messageIds, accesstoken: tokens.access_token });
    }
    return Promise.resolve();
}

function createDraft({ org, tokens, toRecipients, ccRecipients, subject, body, attachment }) {
    if (!org?.integrations?.email?.enabled) return null;

    if (org.integrations.email.supplier === 'outlook') {
        return outlook.createDraft({ accesstoken: tokens.accessToken, toRecipients, ccRecipients, subject, body, attachment });
    } else if (org.integrations.email.supplier === 'gmail') {
        return gmail.createDraft({ accesstoken: tokens.access_token, toRecipients, ccRecipients, subject, body, attachment });
    }
}

function sendEmail({ org, tokens, toRecipients, ccRecipients, bccRecipients, subject, body, attachments = [], monadaAccesstoken, monadaOrganizationId, useQueue = false, generatePdf = null, opportunityId = null, logFollowUp = false, includeSignature = true }) {
    if (!org?.integrations?.email?.enabled) return null;

    // If useQueue is true, send via server-side queue
    if (useQueue) {
        return queueEmail({
            org,
            toRecipients,
            ccRecipients,
            bccRecipients,
            subject,
            body,
            attachments,
            isReply: false,
            conversationId: null,
            generatePdf,
            opportunityId,
            includeSignature,
            logFollowUp,
            monadaAccesstoken
        });
    }

    // Direct send (legacy behavior)
    if (org.integrations.email.supplier === 'outlook') {
        return outlook.sendEmail({ accesstoken: tokens.accessToken, toRecipients, ccRecipients, bccRecipients, subject, body, attachments});
    } else if (org.integrations.email.supplier === 'gmail') {
        return gmail.sendEmail({ accesstoken: tokens.access_token, toRecipients, ccRecipients, bccRecipients, subject, body, attachments, monadaAccesstoken, monadaOrganizationId });
    }
}

function sendReply({ org, tokens, conversationId, messageId, toRecipients, ccRecipients, subject, body, attachments = [], monadaAccesstoken, monadaOrganizationId, useQueue = false, generatePdf = null, opportunityId = null, includeThread = true, includeSignature = true }) {
    if (!org?.integrations?.email?.enabled) return null;

    // If useQueue is true, send via server-side queue
    if (useQueue) {
        return queueEmail({
            org,
            toRecipients,
            ccRecipients,
            subject,
            body,
            attachments,
            isReply: true,
            conversationId,
            generatePdf,
            opportunityId,
            includeThread,
            includeSignature,
            monadaAccesstoken
        });
    }

    // Direct send (legacy behavior)
    if (org.integrations.email.supplier === 'outlook') {
        return outlook.sendReply({ conversationId, accesstoken: tokens.accessToken, toRecipients, ccRecipients, body, attachments });
    } else if (org.integrations.email.supplier === 'gmail') {
        return gmail.sendReply({ conversationId, accesstoken: tokens.access_token, toRecipients, ccRecipients, body, attachments, monadaAccesstoken, monadaOrganizationId });
    }
}

function createDraftReply({ org, tokens, conversationId, messageId, toRecipients, ccRecipients, body, attachment, monadaAccesstoken, monadaOrganizationId }) {
    if (!org?.integrations?.email?.enabled) return null;

    if (org.integrations.email.supplier === 'outlook') {
        return outlook.createDraftReply({ messageId, accesstoken: tokens.accessToken, toRecipients, ccRecipients, body, attachment });
    } else if (org.integrations.email.supplier === 'gmail') {
        return gmail.createDraftReply({ conversationId, accesstoken: tokens.access_token, toRecipients, ccRecipients, body, attachment, monadaAccesstoken, monadaOrganizationId });
    }
}

function getAttachmentUrl({ org, tokens, messageId, attachment, monadaAccesstoken }) {
    if (!org?.integrations?.email?.enabled) return null;
    if (!tokens) throw new Error('Email not connected — cannot download attachment');

    if (org.integrations.email.supplier === 'outlook') {
        return outlook.getAttachmentUrl({ messageId, attachment, monadaOrganizationId: org.id, monadaAccesstoken, accesstoken: tokens.accessToken });
    } else if (org.integrations.email.supplier === 'gmail') {
        return gmail.getAttachmentUrl({ messageId, attachment, monadaOrganizationId: org.id, monadaAccesstoken, accesstoken: tokens.access_token });
    }
}

function archive({ org, tokens, conversationId }) {
    if (!org?.integrations?.email?.enabled) return null;

    if (org.integrations.email.supplier === 'outlook') {
        return outlook.archive({ conversationId, accesstoken: tokens.accessToken });
    } else if (org.integrations.email.supplier === 'gmail') {
        return gmail.archive({ conversationId, accesstoken: tokens.access_token });
    }
}

function trash({ org, tokens, conversationId }) {
    if (!org?.integrations?.email?.enabled) return null;

    if (org.integrations.email.supplier === 'outlook') {
        return outlook.trash({ conversationId, accesstoken: tokens.accessToken });
    } else if (org.integrations.email.supplier === 'gmail') {
        return gmail.trash({ conversationId, accesstoken: tokens.access_token });
    }
}

async function getFolders({ org, tokens }) {
    if (!org?.integrations?.email?.enabled) return null;
    if (org.integrations.email.supplier === 'outlook') {
        return outlook.getFolders({ accesstoken: tokens.accessToken });
    } else if (org.integrations.email.supplier === 'gmail') {
        return gmail.getFolders({ accesstoken: tokens.access_token });
    }
}

// Folders cache - using secure IndexedDB cache
async function getCachedFolders({ encryptionKey, orgId }) {
    const cached = await secureCache.getCache({
        encryptionKey,
        orgId,
        type: 'folders',
        folder: 'all',
        maxAge: CACHE_MAX_AGE
    });
    if (!cached || !cached.data) return null;
    return {
        folders: cached.data.folders,
        labels: cached.data.labels,
        defaultFolderId: cached.data.defaultFolderId,
        timestamp: cached.timestamp,
        isStale: cached.isStale
    };
}

async function setCachedFolders({ encryptionKey, orgId, folders, labels, defaultFolderId }) {
    return secureCache.setCache({
        encryptionKey,
        orgId,
        type: 'folders',
        folder: 'all',
        data: {
            folders,
            labels,
            defaultFolderId
        }
    });
}

async function getDefaultFolderId({ org, folders, tokens }) {
    if (!org?.integrations?.email?.enabled) return null;
    if (org.integrations.email.supplier === 'outlook') {
        return outlook.getDefaultFolderId({ folders, accesstoken: tokens?.access_token });
    } else if (org.integrations.email.supplier === 'gmail') {
        return gmail.getDefaultFolderId({ folders });
    }
}

async function getLabels({ org, tokens }) {
    if (!org?.integrations?.email?.enabled) return null;
    if (org.integrations.email.supplier === 'outlook') {
        return outlook.getLabels({ accesstoken: tokens.accessToken });
    } else if (org.integrations.email.supplier === 'gmail') {
        return gmail.getLabels({ accesstoken: tokens.access_token });
    }
}

function getFlagIcon({ org }) {
    if (!org?.integrations?.email?.enabled) return null;
    if (org.integrations.email.supplier === 'outlook') {
        return {
            filled: faFlag,
            empty: faFlagLight,
            color: red[500],
            title: 'Flagged',
        };
    } else if (org.integrations.email.supplier === 'gmail') {
        return {
            filled: faStar,
            empty: faStarLight,
            color: yellow[600],
            title: 'Starred',
        };
    }
}

async function setConversationFlag({ org, tokens, conversationId, flagged }) {
    if (!org?.integrations?.email?.enabled) return null;
    if (org.integrations.email.supplier === 'outlook') {
        return outlook.setConversationFlag({ conversationId, accesstoken: tokens.accessToken, flagged });
    }
    if (org.integrations.email.supplier === 'gmail') {
        return gmail.setConversationFlag({ conversationId, accesstoken: tokens.access_token, flagged });
    }
}

// Email thread order based on supplier
function getEmailThreadOrder(org) {
    if (!org?.integrations?.email?.supplier) return 'asc'; // Default to asc
    // Gmail: ascending (oldest first), Outlook: descending (newest first)
    return org.integrations.email.supplier === 'gmail' ? 'asc' : 'desc';
}

export default { setConversationsListener, setExpiredListener, refreshEmailConnection, forceRefreshEmailConnection, LoginButton, disconnect,
    getConversations, getConversationsWithCache, getCachedConversations, getMessagesForConversation, createDraft, sendEmail, sendReply, createDraftReply, getAttachmentUrl,
    archive, trash, markAsRead, markAsUnread, getFolders, getCachedFolders, setCachedFolders, getLabels, getFlagIcon, setConversationFlag, getDefaultFolderId,
    getEmailThreadOrder, queueEmail, getQueuedEmails, retryEmail, getEmailStatus, waitForEmailSent };
